/**
 * Activity signals — a presence trace, deliberately without content.
 *
 * `docs/reporting/DESIGN-activity-reporting.md` §1.7 established that this tool records *decisions*
 * and never *presence*: there is no page-load, navigation, read or focus signal anywhere, so the
 * per-person daily report can say what somebody decided and cannot say when they were working. §2
 * designed a full presence stream to close that, whose privacy questions (Q1–Q4) are still open and
 * are the owner's to rule on.
 *
 * This module is the narrow first slice of §2 and nothing more: it reports that a reviewer opened a
 * step in the workflow graph — an act of *reading*, which no existing signal can observe. It is a
 * point event with a server timestamp, so a run of them bounds a working interval that would
 * otherwise be invisible between two far-apart decisions.
 *
 * Three properties are load-bearing, and each has a test that fails if you remove it:
 *
 * - **It degrades to nothing.** Every failure path — offline, 4xx, 5xx, no `fetch` at all, a CSRF
 *   token that will not load — is swallowed. A reviewer must never see a broken screen, a console
 *   error, or a stalled interaction because a measurement did not land. Losing a signal costs a
 *   smudge on a chart; blocking a review costs the review.
 * - **It carries no content.** The step id and the route pattern, never the prompt text, never a
 *   query string, never an item id. §2.3. If you are tempted to add "just the title", that is the
 *   line this comment exists to hold.
 * - **It batches.** Production throttles mutations at 30/min (`app.module.ts`) and a POST is a
 *   mutation, so one request per click would let an ordinary burst of graph-reading eat a
 *   reviewer's whole budget and start 429-ing their real work. Signals are buffered and flushed on
 *   a timer, so a flurry of clicks costs one slot.
 */

import { getCsrfToken } from '../api/client.js';

/**
 * Every kind of reading signal the tool emits.
 *
 * **A union, never a free string**, and each member is a deliberate widening of what is measured
 * about a named colleague. Adding one means adding a case to the server validator too
 * (`activity.types.ts` in the API), which is the point: the type system makes the decision visible
 * in a diff rather than letting a new `kind` string appear in production unannounced.
 *
 * All of these are *navigation and reading* — they say somebody was working with the material, not
 * what they concluded about it. None carries content. The decision an action leads to is already
 * recorded in the audit log, with the item attached; these fill the gap the audit log cannot see,
 * which is the time somebody spends looking before they decide anything (owner instruction,
 * 2026-08-06: extend beyond step expansion to the other navigation controls).
 */
export type ActivitySignalKind =
  /** A step card in the workflow graph was opened to read its full text. */
  | 'graph_step_expanded'
  /** …and closed again. Emitted so a reading stretch has an observed end rather than an assumed one. */
  | 'graph_step_collapsed'
  /** The whole workflow graph strip was shown or hidden. */
  | 'graph_toggled'
  /** The player preview was taken full screen, or brought back. */
  | 'player_fullscreen_toggled'
  /** The reviewer moved to a different item in the queue. */
  | 'item_opened'
  /**
   * One minute of *interactive* presence — see {@link startActivityHeartbeat}.
   *
   * The only kind here that is not a single deliberate gesture, and the only one that can widen the
   * owner-facing working-span figure without the reviewer having decided anything. It is emitted
   * **by** a deliberate interaction and throttled to one per interval — never by a timer looking
   * back at one, which is the design that lost two thirds of its evidence on 2026-08-11. Mere focus
   * is never enough; a tab left open on a second monitor emits nothing.
   */
  | 'active_heartbeat';

export interface ActivitySignal {
  kind: ActivitySignalKind;
  /** Client clock, recorded but never authoritative — the server stamps its own. §2.2. */
  clientTimestamp: string;
  /** Which step was opened. An id, never its text. */
  stepId: number | null;
  /** Route *pattern*, never the live path: no item ids, no query strings. §2.3. */
  route: string;
  /**
   * The work item this signal is about, when there is one. Added 2026-08-12.
   *
   * **This is the join key, and it is deliberately a separate field rather than a relaxation of the
   * route rule.** The route stays a pattern and the server still *refuses* a route carrying a query
   * string or an id — so what changed is one declared, validated, greppable field, not the guarantee
   * around it. The full argument for permitting an id at all, and an explicit list of what it makes
   * newly inferable about a named colleague, is in the API's `activity.types.ts`; read it before
   * adding a second identifier here.
   *
   * Omitted entirely when there is no item, rather than sent as `null`: a heartbeat is not "about
   * item null". The server **refuses** it outright on `active_heartbeat`, `graph_toggled` and
   * `player_fullscreen_toggled` (`ITEM_SCOPED_KINDS`), because tagging ambient presence with an item
   * would let "a browser was open" be joined to a decision as though it were evidence of reading
   * that item.
   */
  workItemId?: string;
}

/**
 * Flush cadence. Long enough that a burst of clicks costs one request, short enough that a tab
 * closed without `pagehide` firing loses at most this much.
 *
 * **Raised from 15 s to 60 s when the heartbeat landed**, on cross-model review. The beacon is a
 * POST and therefore shares the reviewer's 30-per-minute mutation budget (`app.module.ts`) with
 * their actual approvals — so at 15 s a continuously-working tab spent four slots a minute on
 * measurement, and 29 real mutations plus one beacon would have 429'd the next real review action.
 * At 60 s it spends one, matched to the heartbeat cadence so a working reviewer produces one
 * request carrying one heartbeat rather than four carrying nothing.
 */
const FLUSH_INTERVAL_MS = 60_000;

/**
 * Hard ceiling on the buffer. A runaway caller must not grow an unbounded array in the reviewer's
 * tab, and a batch the server would reject as too large is worse than a dropped signal.
 */
const MAX_BUFFERED = 50;

const ENDPOINT = '/api/v1/activity/beacon';

/**
 * Minimum spacing between heartbeats — the throttle, not a tick.
 *
 * Continuous work therefore produces roughly one row a minute, exactly as the old timer did, so
 * every consumer of the cadence is unaffected: `MAX_BUFFERED`, the reviewer's 30-per-minute
 * mutation budget, the sanity check's 3,000-rows-a-day ceiling, and `HEARTBEAT_BRIDGE_MINUTES`
 * (2 minutes, in the API's `work-sessions.ts`) which bridges consecutive samples into a span and
 * needs them closer together than that to do it.
 */
const HEARTBEAT_INTERVAL_MS = 60_000;

/**
 * What counts as evidence that a human is present.
 *
 * **`pointermove` and `scroll` are deliberately absent, and this is the load-bearing choice.** Incidental cursor
 * drift — a hand resting on a trackpad, a notification sliding in, a screen-sharing tool moving the
 * pointer — would re-arm the gate for a full minute with nobody working, and since these samples
 * now widen `bridgedMinutes` (the figure a red/amber/green tile about a named colleague's contract
 * is drawn from) that is the one error direction we cannot afford. Every event here requires a
 * deliberate act: a key, a click, a wheel. `scroll` was removed for the same reason after
 * cross-model review pointed out that it also fires for *programmatic* scrolling — an auto-scrolling
 * panel would then arm the gate on its own, with nobody in the room.
 *
 * The cost is real and accepted: someone reading a long lesson without touching anything for a full
 * minute emits nothing, so the span still under-reports. Under-reporting is the safe direction.
 */
const INTERACTION_EVENTS = ['keydown', 'pointerdown', 'wheel'] as const;

let buffer: ActivitySignal[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let listenersBound = false;
let heartbeatEnabled = false;
let interactionListenersBound = false;
let lastInteractionAt = 0;
/**
 * When the last heartbeat was emitted, on the MONOTONIC clock, for the one-per-interval throttle.
 *
 * **`null` is the "never emitted" sentinel, and it must not be `0`.** `performance.now()` returns
 * exactly `0` at document start — and under fake timers — so a `0` sentinel is a real reading that
 * reads as "never", and the second interaction of a session emits a heartbeat the throttle should
 * have suppressed. Caught by the throttle test, which is why it counts rather than asserting
 * "at least one".
 *
 * Not comparable with `lastInteractionAt`, which is wall-clock.
 */
let lastHeartbeatAt: number | null = null;

/** Elapsed-time clock that cannot step backwards. Falls back where `performance` is absent. */
function monotonicNow(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

/**
 * Records one signal. Returns nothing and throws nothing, by contract — callers are UI event
 * handlers and must not have to think about this at all.
 */
export function recordActivitySignal(signal: Omit<ActivitySignal, 'clientTimestamp' | 'route'>): void {
  try {
    if (buffer.length >= MAX_BUFFERED) {
      return;
    }
    buffer.push({
      ...signal,
      clientTimestamp: new Date().toISOString(),
      route: currentRoutePattern(),
    });
    bindLifecycleListeners();
    scheduleFlush();
  } catch {
    // Deliberately silent. See the module comment: a measurement never breaks a review.
  }
}

/**
 * Sends whatever is buffered. Exported for tests and for the page-hide listener; safe to call when
 * empty.
 *
 * `keepalive: true` rather than `navigator.sendBeacon`: a beacon cannot set headers, so it could
 * not carry the `X-CSRF-Token` that `CsrfGuard` requires on every mutation, and the alternative —
 * exempting this endpoint from CSRF — would let a forged cross-site request write false presence
 * data about a named colleague. A keepalive fetch is delivered after the page is gone in every
 * browser this tool supports, and the token is served from a 5-minute cache that is already warm by
 * the time anyone has opened a step.
 */
export function flushActivitySignals(): void {
  try {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    if (buffer.length === 0) {
      return;
    }
    // Take the batch before sending: if the send throws, these signals are already dropped rather
    // than re-sent forever by a caller that retries.
    const batch = buffer;
    buffer = [];

    if (typeof fetch !== 'function') {
      return;
    }
    void getCsrfToken()
      .then((token) =>
        fetch(ENDPOINT, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
          // `sentAt` is the client's clock at the moment of the flush. The server uses it only as
          // the far end of a difference against each signal's own `clientTimestamp`, so it can place
          // a signal that has been sitting in this buffer for most of a minute at roughly when it
          // happened rather than at when the batch landed — without ever trusting this clock in
          // absolute terms, because a difference of two readings from one clock cancels its error.
          // Without it, an item opened and then approved within the flush interval is stored as
          // having happened AFTER the decision it preceded.
          body: JSON.stringify({ sentAt: new Date().toISOString(), signals: batch }),
          keepalive: true,
        }),
      )
      .catch(() => undefined);
  } catch {
    // As above.
  }
}

/**
 * Starts the interactive-presence heartbeat. Idempotent; returns a stop function.
 *
 * Called once from the app root, so it runs for as long as the tool is open. Every other kind in
 * this module is emitted by a component when the reviewer does something specific; this one is the
 * only ambient emitter, and it exists because the gap it fills is the whole point of the exercise —
 * a reviewer who reads for forty minutes and then approves once leaves a single instant in the
 * record, and the working-span figure reads that day as a single instant of work.
 *
 * **It is emitted BY the interaction, not by a timer that looks back at one** (rewritten
 * 2026-08-13, and the previous design lost two thirds of the evidence — see below). A trusted
 * `keydown`/`pointerdown`/`wheel` emits a heartbeat immediately, unless one was already emitted
 * within {@link HEARTBEAT_INTERVAL_MS}. So the cadence ceiling is unchanged at one per minute, and
 * every row now stands on a real deliberate act *at the instant it is stamped*.
 *
 * **Why the timer had to go, measured on production rather than argued.** On report-day 2026-08-11
 * the reviewer performed 65 decisions across **34 distinct minutes** — every one of them a click,
 * so every one of them a minute this gate was built to credit. **11 heartbeats landed.** The old
 * design sampled on a 60 s `setInterval` grid and required the tab to be *visible at the tick
 * instant*; a reviewer who decides an item and switches to the source PDF in another tab satisfies
 * every part of the safety argument and is still dropped, because the tick lands while he is
 * elsewhere. The 11 stored rows carry **seven distinct grid phases**, which is the same failure
 * seen from the other side: background throttling drags the interval and re-phases it. Emitting on
 * the interaction removes both, because at the moment of a trusted interaction the tab is visible
 * by construction.
 *
 * **The safety argument is strictly stronger than before, not weaker.** The old gate credited a
 * minute on "the tab is visible *now* and something happened *sometime* in the last 60 s"; this one
 * credits only "a person deliberately acted, here". The one thing it stops doing is crediting up to
 * 60 s *after* a reviewer's last interaction — which is quiet time the module already says it does
 * not want to credit.
 *
 * Still true, and still the point: mere focus is never enough, `pointermove` and `scroll` are
 * deliberately not interactions, and a tab left open on a second monitor emits nothing at all.
 */
export function startActivityHeartbeat(): () => void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => undefined;
  }
  heartbeatEnabled = true;
  bindInteractionListeners();
  return stopActivityHeartbeat;
}

/**
 * Stops the heartbeat. Interaction listeners stay bound — they are passive and idempotent, and
 * unbinding them would need the same function reference back; the flag is what actually gates
 * emission, so a stopped heartbeat records nothing however much the reviewer clicks.
 */
export function stopActivityHeartbeat(): void {
  heartbeatEnabled = false;
}

/**
 * Emits at most one heartbeat per {@link HEARTBEAT_INTERVAL_MS}, called from a trusted interaction.
 *
 * The throttle is what bounds the volume: a POST spends one of the reviewer's 30-per-minute
 * mutation slots, so an untrottled emitter would let a burst of typing 429 their actual approvals.
 * `lastHeartbeatAt` starts at `null` so the *first* interaction of a session emits at once rather
 * than after a silent minute — under the old design the first minute of every session was lost,
 * along with every session shorter than a minute.
 *
 * The `repeat` and `isTrusted` filters in front of this both live in the real listener, which jsdom
 * cannot exercise: it cannot construct a trusted event at all. That limitation is the same one
 * documented on `isTrusted` and is not specific to the autorepeat guard.
 */
function emitHeartbeatIfDue(): void {
  try {
    if (!heartbeatEnabled) {
      return;
    }
    // Belt and braces, and genuinely a no-op: a trusted pointer/key/wheel event cannot reach a
    // hidden tab. It stays because it costs nothing and it is the invariant the safety argument
    // rests on, so it should fail closed if a browser ever violates it.
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return;
    }
    // **Monotonic, not wall-clock.** `Date.now()` steps when the OS corrects the clock or the
    // machine wakes from sleep, and a backward step would stall the heartbeat for as long as the
    // correction while a forward one would let it emit again early. `performance.now()` cannot
    // step, so the throttle measures elapsed time rather than clock arithmetic.
    const now = monotonicNow();
    if (lastHeartbeatAt !== null && now - lastHeartbeatAt < HEARTBEAT_INTERVAL_MS) {
      return;
    }
    lastHeartbeatAt = now;
    recordActivitySignal({ kind: 'active_heartbeat', stepId: null });
  } catch {
    // Same contract as everything else here: a measurement never breaks a review.
  }
}

function bindInteractionListeners(): void {
  if (interactionListenersBound || typeof window === 'undefined') {
    return;
  }
  interactionListenersBound = true;
  const mark = (event: Event) => {
    // **`isTrusted` only.** A synthetic event created by page script is not a person, and this gate
    // is the sole thing standing between "a browser is open" and a number that credits somebody's
    // working day. It does not defend against WebDriver or OS-level input injection, which are
    // indistinguishable from a human by construction — but it does remove the cheapest way to
    // fabricate presence, which is a line of JavaScript in the console.
    if (!event.isTrusted) {
      return;
    }
    // **A held key is one act, not forty.** `keydown` autorepeats while a key is down — trusted,
    // indistinguishable from typing by `isTrusted` alone — so a key resting under a book would
    // re-arm this gate indefinitely. The throttle bounds how many rows that becomes, but not
    // whether they are evidence, and evidence is the whole point of the gate.
    if ('repeat' in event && (event as KeyboardEvent).repeat) {
      return;
    }
    lastInteractionAt = Date.now();
    emitHeartbeatIfDue();
  };
  for (const eventName of INTERACTION_EVENTS) {
    // `passive` so none of this can delay scrolling, and `capture` so an interaction is observed
    // even where a component stops propagation — a reviewer whose clicks are all swallowed by a
    // widget would otherwise look idle while working.
    window.addEventListener(eventName, mark, { passive: true, capture: true });
  }
}

/**
 * Test seam: arms the interaction gate as a real, trusted interaction would.
 *
 * It exists because **jsdom cannot create a trusted event** — `isTrusted` is a non-configurable own
 * property fixed at `false` — so there is no way to exercise the positive path from a test. The
 * negative path needs no seam and is tested directly: a dispatched (therefore untrusted) event must
 * NOT arm the gate, which is the security property this is all about.
 */
export function markInteractionForTest(): void {
  lastInteractionAt = Date.now();
  emitHeartbeatIfDue();
}

/** Test seam: drops anything buffered without sending it, and stops the heartbeat. */
export function resetActivitySignalsForTest(): void {
  buffer = [];
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  stopActivityHeartbeat();
  lastInteractionAt = 0;
  lastHeartbeatAt = null;
}

function scheduleFlush(): void {
  if (timer !== null) {
    return;
  }
  timer = setTimeout(() => {
    timer = null;
    flushActivitySignals();
  }, FLUSH_INTERVAL_MS);
}

function bindLifecycleListeners(): void {
  if (listenersBound || typeof window === 'undefined') {
    return;
  }
  listenersBound = true;
  // `pagehide` rather than `unload`: `unload` is not fired at all on modern mobile Safari and
  // disqualifies the page from the back/forward cache.
  window.addEventListener('pagehide', () => flushActivitySignals());
  window.addEventListener('visibilitychange', () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      flushActivitySignals();
    }
  });
}

/**
 * The route with its identifiers replaced — `/queue/practice/elpi-l1-...` becomes
 * `/queue/practice/:id`. The audit log already records which item somebody decided on, with the
 * decision attached; the presence record does not need it a second time (§2.3).
 */
function currentRoutePattern(): string {
  if (typeof window === 'undefined') {
    return 'unknown';
  }
  const path = window.location.pathname || '/';
  return (
    path
      .split('/')
      .map((segment) => (looksLikeIdentifier(segment) ? ':id' : segment))
      .join('/') || '/'
  );
}

function looksLikeIdentifier(segment: string): boolean {
  if (segment.length === 0) return false;
  // Anything numeric, long, or carrying a separator is treated as an id. Erring towards masking is
  // the safe direction: a route that reads `:id` when it did not need to costs nothing, while a
  // leaked item id defeats the point of not recording one.
  return /\d/.test(segment) || segment.length > 24 || segment.includes('-') || segment.includes('_');
}
